/**
 * Billing: the subscription row is read straight from PocketBase (users can only see their own);
 * Checkout and the customer portal go through services/billing, which holds the Stripe key.
 */
import { ClientResponseError } from "pocketbase";
import { env } from "@/lib/core/env";
import type { SubscriptionsResponse } from "@/lib/core/pb-types";
import { pb } from "@/lib/core/pocketbase";

export type Subscription = SubscriptionsResponse;
export type Interval = "month" | "year";
export type PlanPrice = {
  /** Display amounts per interval (a year is billed at once); Stripe charges the real ones. */
  eur: number;
  usd: number;
  /** False when the Stripe price id is missing from the service's env. */
  configured: boolean;
};
/** A paid plan as services/billing lists it (`free` has no price and is not listed). */
export type Plan = { id: "starter" | "pro"; name: string; prices: Record<Interval, PlanPrice> };

export type BillingCurrency = "EUR" | "USD";

/** Billed in euros: Europe. Every other country pays in US dollars (the prices' USD option). */
const EUR_COUNTRIES = new Set(
  (
    "AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK " +
    "AD AL BA CH GB GE IS LI MC MD ME MK NO RS SM TR UA VA XK"
  ).split(" "),
);

/**
 * The currency the plans page shows and Checkout charges: the company's country of registration
 * decides, like everything else about the business. Without one yet, the browser's region.
 */
export function billingCurrency(
  country: string | undefined,
  language = navigator.language,
): BillingCurrency {
  if (country) return EUR_COUNTRIES.has(country) ? "EUR" : "USD";
  return /-US$/i.test(language) ? "USD" : "EUR";
}

// Same as the server (lib/plans.js): a failed renewal keeps the plan while Stripe retries.
const ACTIVE = new Set(["active", "trialing", "past_due"]);

export function hasActivePlan(subscription: Subscription | null | undefined): boolean {
  return Boolean(subscription && ACTIVE.has(subscription.status));
}

export async function getMySubscription(userId: string): Promise<Subscription | null> {
  try {
    return await pb
      .collection("subscriptions")
      .getFirstListItem(pb.filter("user = {:userId}", { userId }));
  } catch (error) {
    if (error instanceof ClientResponseError && error.status === 404) return null;
    throw error;
  }
}

function headers(): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${pb.authStore.token}`,
  };
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // ignore, fall through
  }
  return `Billing request failed (${res.status})`;
}

export async function listPlans(): Promise<Plan[]> {
  const res = await fetch(`${env.billingUrl}/plans`, { headers: headers() });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { plans: Plan[] };
  return body.plans;
}

/**
 * Returns the URL to send the browser to: Stripe Checkout for a new subscription, or the portal's
 * confirmation page when an active subscriber switches plan or interval.
 */
export async function createCheckout({
  plan,
  interval,
  currency,
}: {
  plan: string;
  interval: Interval;
  currency: BillingCurrency;
}): Promise<string> {
  const res = await fetch(`${env.billingUrl}/checkout`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ plan, interval, currency }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { url: string };
  return body.url;
}

/** Returns the customer portal URL (manage payment method, cancel, invoices). */
export async function createPortal(): Promise<string> {
  const res = await fetch(`${env.billingUrl}/portal`, { method: "POST", headers: headers() });
  if (!res.ok) throw new Error(await errorMessage(res));
  const body = (await res.json()) as { url: string };
  return body.url;
}

/** Ends the subscription right away (account deletion); fine when there is none. */
export async function cancelSubscription(): Promise<void> {
  const res = await fetch(`${env.billingUrl}/cancel`, { method: "POST", headers: headers() });
  if (!res.ok) throw new Error(await errorMessage(res));
}

export const CANCEL_REASONS = [
  "too_expensive",
  "missing_features",
  "unused",
  "switched_service",
  "other",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

async function post(path: string, body: unknown = {}): Promise<void> {
  const res = await fetch(`${env.billingUrl}/${path}`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
}

/** The cancellation flow: ends the plan when the paid period is over, with the survey's answer. */
export const cancelAtPeriodEnd = (reason: CancelReason, comment: string) =>
  post("cancel-at-period-end", { reason, comment });
/** Takes back a cancellation at the period end. */
export const keepSubscription = () => post("keep");
/** No charges (and no paid plan) for three months instead of cancelling. */
export const pauseSubscription = () => post("pause");
export const resumeSubscription = () => post("resume");

/** A renewal failed and Stripe is retrying (past_due) or gave up (unpaid): the card needs updating. */
export const paymentFailed = (subscription: Subscription | null | undefined) =>
  subscription?.status === "past_due" || subscription?.status === "unpaid";
